import { SettingsPermissions } from "@redotech/account-common/permissions";
import { directMerchantAccess } from "../../settings-catalog/access";
import type {
  SettingGroupDefinition,
  SettingOwner,
  SettingRisk,
} from "../../settings-catalog/types";

export type CustomerPortalFlatTeamSettingGroupId =
  | "customer_portal.customize"
  | "customer_portal.flows"
  | "customer_portal.ai_exchange_agent"
  | "customer_portal.exchange_settings"
  | "customer_portal.localization"
  | "customer_portal.predefined_categories"
  | "customer_portal.discount_distribution"
  | "customer_portal.bundles";

const customerportalOwner: SettingOwner = {
  kind: "teamSettings",
  product: "returns",
};

const customerportalRisk: SettingRisk = {
  level: "medium",
  reason: "Customer portal configuration is customer-facing.",
};

export const CustomerPortalFlatTeamSettingGroups = [
  {
    id: "customer_portal.customize",
    title: "Customize portal",
    description: "Customize the customer portal appearance and content.",
    productArea: "returns",
    access: directMerchantAccess(
      SettingsPermissions.SET_RETURNS_AND_CLAIMS_SETTINGS,
    ),
    owner: customerportalOwner,
    risk: customerportalRisk,
    agentEditability: { mode: "requiresApproval" },
    fullPageTarget: { type: "settings", path: "customer-portal" },
    synonyms: ["customize portal", "portal appearance", "portal customization"],
  },
  {
    id: "customer_portal.flows",
    title: "Portal flows",
    description:
      "Return, claim, and warranty flows shown in the customer portal.",
    productArea: "returns",
    access: directMerchantAccess(
      SettingsPermissions.SET_RETURNS_AND_CLAIMS_SETTINGS,
    ),
    owner: customerportalOwner,
    risk: customerportalRisk,
    agentEditability: { mode: "requiresApproval" },
    fullPageTarget: { type: "settings", path: "customer-portal" },
    synonyms: ["portal flows", "return flows", "customer flows"],
  },
  {
    id: "customer_portal.ai_exchange_agent",
    title: "AI exchange agent",
    description: "AI exchange agent settings for the customer portal.",
    productArea: "returns",
    access: directMerchantAccess(
      SettingsPermissions.SET_RETURNS_AND_CLAIMS_SETTINGS,
    ),
    owner: customerportalOwner,
    risk: customerportalRisk,
    agentEditability: { mode: "requiresApproval" },
    fullPageTarget: { type: "settings", path: "customer-portal" },
    synonyms: ["ai exchange agent", "exchange agent", "ai exchanges"],
  },
  {
    id: "customer_portal.exchange_settings",
    title: "Exchange settings",
    description: "Exchange configuration for the customer portal.",
    productArea: "returns",
    access: directMerchantAccess(
      SettingsPermissions.SET_RETURNS_AND_CLAIMS_SETTINGS,
    ),
    owner: customerportalOwner,
    risk: customerportalRisk,
    agentEditability: { mode: "requiresApproval" },
    fullPageTarget: { type: "settings", path: "customer-portal" },
    synonyms: ["exchange settings", "exchanges", "portal exchanges"],
  },
  {
    id: "customer_portal.localization",
    title: "Localization",
    description: "Localization and language settings for the customer portal.",
    productArea: "returns",
    access: directMerchantAccess(
      SettingsPermissions.SET_RETURNS_AND_CLAIMS_SETTINGS,
    ),
    owner: customerportalOwner,
    risk: customerportalRisk,
    agentEditability: { mode: "requiresApproval" },
    fullPageTarget: { type: "settings", path: "customer-portal" },
    synonyms: ["localization", "language", "languages", "translations"],
  },
  {
    id: "customer_portal.predefined_categories",
    title: "Predefined categories",
    description:
      "Predefined return/exchange categories for the customer portal.",
    productArea: "returns",
    access: directMerchantAccess(
      SettingsPermissions.SET_RETURNS_AND_CLAIMS_SETTINGS,
    ),
    owner: customerportalOwner,
    risk: customerportalRisk,
    agentEditability: { mode: "requiresApproval" },
    fullPageTarget: { type: "settings", path: "customer-portal" },
    synonyms: ["predefined categories", "categories", "return reasons"],
  },
  {
    id: "customer_portal.discount_distribution",
    title: "Discount distribution",
    description: "Discount distribution settings for the customer portal.",
    productArea: "returns",
    access: directMerchantAccess(
      SettingsPermissions.SET_RETURNS_AND_CLAIMS_SETTINGS,
    ),
    owner: customerportalOwner,
    risk: customerportalRisk,
    agentEditability: { mode: "requiresApproval" },
    fullPageTarget: { type: "settings", path: "customer-portal" },
    synonyms: ["discount distribution", "portal discounts", "distribution"],
  },
  {
    id: "customer_portal.bundles",
    title: "Bundles",
    description: "Bundle settings for the customer portal.",
    productArea: "returns",
    access: directMerchantAccess(
      SettingsPermissions.SET_RETURNS_AND_CLAIMS_SETTINGS,
    ),
    owner: customerportalOwner,
    risk: customerportalRisk,
    agentEditability: { mode: "requiresApproval" },
    fullPageTarget: { type: "settings", path: "customer-portal" },
    synonyms: ["bundles", "portal bundles", "bundle settings"],
  },
] as const satisfies readonly SettingGroupDefinition<CustomerPortalFlatTeamSettingGroupId>[];

type _assertAllCustomerPortalFlatTeamSettingGroupsProvided =
  CustomerPortalFlatTeamSettingGroupId extends (typeof CustomerPortalFlatTeamSettingGroups)[number]["id"]
    ? true
    : never;
const _allCustomerPortalFlatTeamSettingGroupsProvided: _assertAllCustomerPortalFlatTeamSettingGroupsProvided = true;
void _allCustomerPortalFlatTeamSettingGroupsProvided;

export const CustomerPortalFlatTeamSettingGroupById: Record<
  CustomerPortalFlatTeamSettingGroupId,
  SettingGroupDefinition<CustomerPortalFlatTeamSettingGroupId>
> = (() => {
  const map: Partial<
    Record<
      CustomerPortalFlatTeamSettingGroupId,
      SettingGroupDefinition<CustomerPortalFlatTeamSettingGroupId>
    >
  > = {};
  for (const group of CustomerPortalFlatTeamSettingGroups) {
    map[group.id] = group;
  }
  return map as Record<
    CustomerPortalFlatTeamSettingGroupId,
    SettingGroupDefinition<CustomerPortalFlatTeamSettingGroupId>
  >;
})();
